import {
    BookingType,
    Status as BookingStatus,
} from '../../feature/enums/booking';
import {
    ServiceType,
    Status as JobStatus,
} from '../../feature/enums/contractor';
import {
    ActivityType,
    BoatType,
    CommercialType,
    ExperienceType,
    PropertyType,
    VehicleType,
} from '../../feature/enums/experience';
import { Period } from '../../feature/enums/reports';
import { State } from '../../feature/types/generic';
import { ListBooking } from '../../feature/types/reservation';
import { ExperienceDetails } from '../../feature/types/experience';
import { Step } from '../../feature/components/Experiences/Form/useFormState';

export type XeroConsentUrl = {
    url: string;
};

export type XeroConnection = {
    orgName: string;
};

export type XeroSettings = {
    billsAccountCode: string;
    invoicesAccountCode: string;
};

export type XeroAccount = {
    code: string;
    name: string;
};

export enum ModuleType {
    Boat = 'boat',
    Property = 'property',
    Activity = 'activity',
    Vehicle = 'vehicle',
    Commercial = 'commercial',
}
export enum BookerType {
    Guest = 'guest',
    Owner = 'owner',
}

export type Earnings = {
    bookingsTotal: number;
    serviceFeeAmount: number;
    amount: number;
    amountPaid: number;
    amountDue: number;
    bills: HostPayment[];
    expensesTotal: number;
    refundsTotal: number;
    billingExcludedBookingsTotal: number;
    guestServiceFeeAmount: number;
};

export type GuestInvoice = {
    id: number;
    guestId: number;
    guestName: string;
    year: number;
    month: number;
    from: string;
    to: string;
    dueDate: string;
    total: number;
    amountDue: number;
    amountPaid: number;
    firstName: string;
    lastName: string;
    email: string;
    country: string;
    state: string;
    suburb: string;
    streetAddress: string;
    postcode: string;
    url: string;
    bookings: GuestInvoiceReservation[];
};

export type HostPayment = {
    id: number;
    xeroInvoiceId?: string;
    periodStart: string;
    periodEnd: string;
    month: number;
    year: number;
    bookingsTotal: number;
    serviceFeeAmount: number;
    expensesTotal: number;
    refundTotal: number;
    amount: number;
    amountPaid: number;
    amountDue: number;
    paidAt: string;
    bookings: HostPaymentReservation[];
    ownerId: number;
    ownerName: string;
    expenses: HostPaymentExpenses[];
    billingExcludedBookingsTotal: number;
    guestServiceFeeAmount: number;
};
export type HostPaymentReservation = {
    id: number;
    moduleType: ModuleType;
    moduleName: string;
    bookerType: BookerType;
    bookedFor: string;
    guestName: string;
    startDate: string;
    endDate: string;
    serviceFeePercentage: number;
    amount: number;
    refund: number;
    billingExcluded: any;
};

export type HostPaymentExpenses = {
    id: number;
    amount: number;
    attachments: [];
    billId: number;
    createdBy: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
    };
    date: Date;
    bookingId: number;
    description: string;
    guest: { id: number; name: string };
    hostPaidAt: Date;
    invoiceId: number;
    module: { id: number; name: string; type: string };
    name: string;
    owner: { id: number; name: string };
};
export type GuestInvoiceReservation = {
    id: number;
    moduleType: ModuleType;
    moduleName: string;
    owner: string;
    startDate: string;
    endDate: string;
    booker: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    };
    createdBy: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    };
};

export type ChatEnquiry = {
    moduleId: number;
    message: string;
    isPrivate: boolean;
};
export type ReservationChat = {
    message: string;
    isPrivate: boolean;
    bookingId: number;
};

export type JobChat = {
    message: string;
} & JoinJobChannel;

export type JoinBookingChannel = {
    bookingId: number;
    channelId: string;
};

export type JoinJobChannel = {
    jobId: number;
};

export type Channel = {
    channelId: string;
};

export type UserPreferences = {
    newMessageEmailNotification: boolean;
    newMessagePushNotification: boolean;
    reminderEmailNotification: boolean;
    reservationEmailNotification: boolean;
    reservationPushNotification: boolean;
    reservationAppNotification: boolean;
    accessRequestEmailNotification: boolean;
    accessRequestPushNotification: boolean;
    accessRequestAppNotification: boolean;
    reservationNotificationModules: Array<{ id: number }>;
};

export type OwnerPreferences = {
    requestReminderInterval: number;
};

export type CompanyAccount = {
    email: string;
};

export type OwnerMessagingUserModule = {
    id: number;
};

export type OwnerMessagingUser = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    modules: OwnerMessagingUserModule[];
};

export type OwnerCompany = {
    id: number;
    name: string;
    primaryContact: string;
    isActive: boolean;
    phone: string;
    countryCode: string;
    state: string;
    suburb: string;
    streetAddress: string;
    postcode: string;
    notificationsEmail: string;
    allowsMultipleDomains: boolean;
    account: CompanyAccount;
    messagingUsers?: OwnerMessagingUser[];
    xeroContactId?: string | null;
    activeUsers?: number;
};
export type ContractorPreferences = {
    requestReminderInterval: number;
};

export type InviteContractor = {
    contractorId?: number;
    contractor?: {
        name: string;
        email: string;
        primaryContact: string;
    };
    permissions: 'disabled' | 'readOnly' | 'readAndWrite';
};
export type ApplyDiscountToBooking = {
    percentage: number;
};

export type CustomServiceValidationRequest = {
    serviceType: string;
    label: string;
    existingItems?: string[];
};

export type CustomServiceValidation = {
    status: 'valid' | 'typo' | 'invalid';
    normalized: string;
    suggestion: string;
    reason: string;
    validationToken?: string;
    suggestionToken?: string;
};

export type UpdateJob = {
    jobDate: string;
    services: Array<string>;
    ownerNotes: string;
};

export type JobItem = {
    id?: number;
    template: { id: number; name: string };
    name: string;
    description: string;
    cost: number;
    pricingItem?: { id: number };
    quantity: number;
    total: number;
};

export type JobRequest = {
    booking: { id: number };
    contractor: {
        id: number;
    };
    jobDate: string;
    startTime?: string;
    endTime?: string;
    items: Array<JobItem>;
    services: Array<string>;
    ownerNotes: string;
    providerType?: ProviderType;
};

export type RecurringJobRequest = {
    contractor: {
        id: number;
    };
    modules: Array<{ id: number }>;
    services: Array<string>;
    jobDay: string;
    notes: string;
    enabled: boolean;
};

export enum ProviderType {
    Contractor = 'contractor',
    Owner = 'owner',
}

export type ContractorPolicy = {
    checked: boolean;
    checkedAt: string;
    checkedBy: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    };
    cancellationPolicy: string;
};

export enum JobCreatedBy {
    Owner = 'owner',
    Guest = 'guest',
}

export type Job = {
    id: number;
    serviceType: ServiceType;
    providerType: ProviderType.Contractor;
    assignee: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    } | null;
    channelId?: string;
    module: {
        id: number;
        name: string;
        messagingUsers: Array<{ id: number }>;
        streetAddress: string;
        suburb: string;
        state: State;
        countryCode: 'AU';
        usesNirovision: boolean;
        usesVehicleRec: boolean;
    };
    createdBy: JobCreatedBy;
    contractor: {
        id: number;
        name: string;
        permissions: 'disabled' | 'readOnly' | 'readAndWrite';
    };
    modules?: [
        {
            id: number;
            name: string;
        },
    ];
    booking: {
        id: number;
        startDate: string;
        endDate: string;
        guest?: {
            guests: number;
            checkinTime: string;
            checkoutTime: string;
        };
        booker: {
            id: number;
            firstName: string;
            lastName: string;
            email: string;
        };
    };
    owner: {
        id: number;
        name: string;
    };
    status: JobStatus;
    contractorNotes: string;
    guestNotes?: string;
    jobCompletion: JobCompletion;
    recurringId: number;
    policy?: ContractorPolicy;
    /** ISO date string */
    completedAt: string | null;
    markedCompleteBy: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        companyName: string;
    } | null;
} & JobRequest;

export type RecurringJob = {
    id: number;
    contractor: {
        id: number;
        name: string;
    };
    modules?: [
        {
            id: number;
            name: string;
        },
    ];
    services: [];
    jobDay: string;
    enabled: boolean;
    notes: string;
};

export type ContractorNotes = {
    id: number;
    contractorNotes: string;
};

export type JobCompletion = {
    notes: string;
    beforePhotos: Array<{ id: string; src: string }>;
    afterPhotos: Array<{ id: string; src: string }>;
    jobCompleted: boolean;
};

export type JobAssignee = {
    id: number;
    assignee: { id: number };
};

export type ModuleOccupancy = {
    id: number;
    name: string;
    bookings: number;
    occupiedNights: number;
    totalNights: number;
    totalNightsOccupancy: number;
    occupancy: Array<Occupancy>;
    weeklyOccupancy: {
        monday: number;
        tuesday: number;
        wednesday: number;
        thursday: number;
        friday: number;
        saturday: number;
        sunday: number;
    };
} & Omit<Occupancy, 'periodStart' | 'periodEnd'>;

export type Occupancy = {
    periodStart: string;
    periodEnd: string;
    availableNights: number;
    availableNightsOccupancy: number;
    availableOccupiedNights: number;
};

export type ApproveReservation = {
    bookingIds: number[];
};

export type CancelReservation = {
    reason: string;
} & ApproveReservation;

export type DeclineReservation = {
    reason: string;
    comment: string;
} & ApproveReservation;

export type ReassignReservation = {
    bookerId: number;
} & ApproveReservation;

export type BulkActionResponse = {
    succeeded: ListBooking[];
    failed: ListBooking[];
};

export type BookingArgs = {
    startDate?: Period;
    endDate?: Period;
    activeDate?: Period;
    status?: Array<BookingStatus>;
    bookingType?: Array<BookingType>;
    moduleType?: Array<ExperienceType>;
    guest?: number;
    visitType?: 'business' | 'personal';
    bookerType?: 'guest' | 'owner';
    module?: number;
    scheduledAt?: string;
    sort?: 'startDate';
};

export enum WaitListRequestStatus {
    Active = 'active',
    Cancelled = 'cancelled',
    Expired = 'expired',
    Converted = 'converted',
}

export interface CreateWaitListRequest {
    id?: number;
    checkinTime: string;
    checkoutTime: string;
    startDate: string;
    endDate: string;
    notes: string;
    status: WaitListRequestStatus;
    module: { type: string; id: number };
    commercialWaitListRequestInfo?: {
        desks: number;
        meetingRooms: number;
        privateOffices: number;
        entireVenue: boolean;
    };
}
export interface WaitListRequest extends CreateWaitListRequest {
    id: number;
    status: WaitListRequestStatus;
}
export interface WaitListRequestDetailed extends WaitListRequest {
    isAvailableToBook: boolean;
    createdBy?: {
        firstName?: string;
        lastName?: string;
        email?: string;
        id?: number;
    };
    guest?: {
        id?: number;
        name?: string;
    };
}

export interface UpdateWaitListRequest extends Partial<CreateWaitListRequest> {}

export type PricingTemplateOrderRequest = {
    order: number[];
};

export type NiroJobAccess = {
    jobId: number;
    userId: number;
};

export type NirovisionAccessRequestStatus = 'pending' | 'approved' | 'denied';
export type NirovisionAccessRequestAction = 'approve' | 'deny';
export type NirovisionAccessMode = 'reservation' | 'one_time' | 'day_pass';

export type NirovisionAccessRequest = {
    id: number;
    bookingId: number;
    nirovisionIdentityId: string;
    status: NirovisionAccessRequestStatus;
    identityName: string;
    identityEmail: string;
    userId?: number;
    profilePicture?: string;
    moduleName: string;
    createdAt: string;
    addToReservation?: boolean;
    accessMode?: NirovisionAccessMode;
    needsProfilePicture: boolean;
    needsUser: boolean;
    hasEventImage: boolean;
};

export type NirovisionAccessRequestFace = {
    id: string;
};

export type NirovisionAccessRequestUser = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    hasPhoto: boolean;
    profilePicture?: string;
};

export type UploadGuideRequest = {
    ownerId: string;
    id: string;
    fileUrl: string;
    name: string;
    mimeType: string;
    /** thumbnail src */
    thumbnail?: string;
};

type CreateActivityExperience = {
    type: ExperienceType.Activity;
    details: {
        subType: ActivityType;
    };
};

type CreateBoatExperience = {
    type: ExperienceType.Boat;
    details: {
        subType: BoatType;
    };
};

type CreateCommercialExperience = {
    type: ExperienceType.Commercial;
    details: {
        subType: CommercialType;
    };
};

type CreatePropertyExperience = {
    type: ExperienceType.Property;
    details: {
        subType: PropertyType;
    };
};

type CreateVehicleExperience = {
    type: ExperienceType.Vehicle;
    details: {
        subType: VehicleType;
    };
};

export type CreateExperience =
    | CreateActivityExperience
    | CreateBoatExperience
    | CreateCommercialExperience
    | CreatePropertyExperience
    | CreateVehicleExperience;

export type UpdateExperience = {
    type: ExperienceType;
    currentStep?: Step;
} & Partial<ExperienceDetails>;

export type ExperienceResponse = {
    id: number;
    status: 'draft';
    type: ExperienceType;
};

export type GetIndividualOwnerAssetsParams = {
    visitType?: string;
    type?: string | string[];
    state?: string[];
    suburb?: string[];
    guests?: string;
    overnightGuests?: string;
    bedrooms?: string;
    amenities?: string[];
    checkinDate?: string;
    checkoutDate?: string;
    sort?: 'name';
    swLat?: number;
    swLng?: number;
    neLat?: number;
    neLng?: number;
    rating?: string;
    wishlist?: boolean;
    status?: 'draft' | 'published' | ('draft' | 'published')[];
};

export type UpdateOwnerProfileRequest = {
    ownerId: number;
    biography?: string;
    logo?: {
        id: string;
    };
    websiteUrl?: string;
    instagramUrl?: string;
    facebookUrl?: string;
    linkedinUrl?: string;
    youtubeUrl?: string;
};
